import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth/useLoggedInEmployeeId';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig/useApplicationConfig';
import { TabModules, useModulesConfig } from '~/data/company';
import { PeopleTabs } from '~/data/tabs';
import { useSelectedEmployeeDetails } from '~/hooks/employee/useSelectedEmployeeDetails';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';

type PropsType = {
  employeeId: number;
};

export type UseAbsencePermissionsOutput = {
  canApproveAbsence: boolean;
  canAddAbsence: boolean;
  canEditAbsences: boolean;
  canEditSelectedAbsence: (absenceId: string) => boolean;
};

export const useAbsencePermissions = (
  props: PropsType,
): UseAbsencePermissionsOutput => {
  const { employeeId } = props;

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { isModuleTabEnabled } = useModulesConfig();

  const { applicationConfig } = useApplicationConfig();
  const { isDataInSync, hasRoleAccessOverEmployee } =
    useSelectedEmployeeDetails({
      employeeId,
    });

  const { absenceEntries } = useEmployeeAbsenceData({ employeeId });

  const { hasOwnAccess, hasFullAccess } = useMemo(() => {
    const absencesEditDisabled =
      applicationConfig?.fComConfig?.absConfig?.fComDisableAbsenceEditing;

    const isAbsencesTabEnabled = isModuleTabEnabled(
      TabModules.PEOPLE,
      PeopleTabs.Absences,
    );

    if (absencesEditDisabled || !isAbsencesTabEnabled) {
      return {
        hasOwnAccess: false,
        hasFullAccess: false,
      };
    }

    return {
      hasOwnAccess: employeeId === loggedInEmployeeId,
      hasFullAccess:
        isDataInSync &&
        hasRoleAccessOverEmployee(employeeModulesAccessRoles.people.full),
    };
  }, [
    employeeId,
    isDataInSync,
    hasRoleAccessOverEmployee,
    loggedInEmployeeId,
    applicationConfig,
    isModuleTabEnabled,
  ]);

  const canEditSelectedAbsence = useCallback(
    (absenceId) => {
      const absence = absenceEntries.find((entry) => entry.uid === absenceId);
      if (!absence) {
        return false;
      }

      return hasFullAccess || (hasOwnAccess && absence.status === 'PROPOSED');
    },
    [absenceEntries, hasFullAccess, hasOwnAccess],
  );

  return {
    canApproveAbsence: hasFullAccess && !hasOwnAccess,
    canAddAbsence: hasFullAccess || hasOwnAccess,
    canEditAbsences: hasFullAccess || hasOwnAccess,
    canEditSelectedAbsence,
  };
};
